import { useCallback, useEffect, useMemo, useState } from "react";
import {
	Activity,
	ArrowUpCircle,
	Edit,
	EyeOff,
	Loader2,
	Plus,
	RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { DOMAIN_PHYSICAL } from "../../lib/domainAccess";
import { fetchAPI } from "../../lib/utils";
import { DeactivateSportDialog } from "../sports/DeactivateSportDialog";
import {
	EMPTY_SPORT_FORM,
	SportDisciplineForm,
	sportToForm,
} from "../sports/SportDisciplineForm";
import { sportConflictMessage } from "../sports/sportConflict";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const FILTERS = [
	["all", "adminSportsFilterAll"],
	["personal", "adminSportsFilterPersonal"],
	["inactive", "adminSportsFilterInactive"],
];

const FAMILY_KEYS = {
	physical: "sdfFamilyPhysical",
	shooting: "sdfFamilyShooting",
};

const matchesFilter = (sport, filter) => {
	if (filter === "personal") return sport.library_scope === "personal";
	if (filter === "inactive") return sport.is_active === false;
	return true;
};

/**
 * Tab admin «Discipline»: il catalogo degli sport (docs/sport-config, Fase 3).
 *
 * Era inline in `AdminDashboard.js`, con una cancellazione che lasciava orfani
 * atleti, modelli e classifiche (B12) e uno slug rinominabile (B13). Ora:
 *
 *  - ogni sport dice **di chi è** (della piattaforma, o personale di un coach)
 *    e **chi lo usa** (atleti, coach, modelli dei coach);
 *  - gli sport creati dai coach (D1) si filtrano, e si rendono globali con un
 *    click: lo slug resta quello, niente da riscrivere;
 *  - non si cancella niente: si disattiva, vedendo prima chi lo usa, e si
 *    riattiva (D5). Lo slug si sceglie solo creando.
 *
 * Carica i suoi dati da sé, come gli altri pannelli in `components/admin/`.
 */
export function SportsAdminTab() {
	const { t } = useLanguage();
	const [sports, setSports] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState("all");
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(EMPTY_SPORT_FORM);
	const [saving, setSaving] = useState(false);
	const [busyId, setBusyId] = useState(null);
	const [deactivating, setDeactivating] = useState(null);

	const load = useCallback(async () => {
		try {
			const data = await fetchAPI("/admin/sports");
			setSports(Array.isArray(data) ? data : []);
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setLoading(false);
		}
	}, [t]);

	useEffect(() => {
		load();
	}, [load]);

	const counts = useMemo(() => {
		const out = {};
		FILTERS.forEach(([key]) => {
			out[key] = sports.filter((sport) => matchesFilter(sport, key)).length;
		});
		return out;
	}, [sports]);
	const shown = sports.filter((sport) => matchesFilter(sport, filter));

	const resetForm = () => {
		setEditing(null);
		setForm({ ...EMPTY_SPORT_FORM, order: sports.length });
	};

	const startEdit = (sport) => {
		setEditing(sport);
		setForm(sportToForm(sport));
	};

	const save = async () => {
		const label = (form.label || "").trim();
		if (!label) return;
		const order = Number.isFinite(Number(form.order)) ? Number(form.order) : 0;
		const families = Array.isArray(form.families) ? form.families : [];
		setSaving(true);
		try {
			if (editing) {
				// Niente `value`: lo slug non cambia (D5), il server lo rifiuterebbe.
				await fetchAPI(`/admin/sports/${encodeURIComponent(editing.sport_id)}`, {
					method: "PUT",
					body: JSON.stringify({ label, order, families }),
				});
				toast.success(t("adminSportsUpdated").replace("{label}", label));
			} else {
				await fetchAPI("/admin/sports", {
					method: "POST",
					body: JSON.stringify({ label, value: form.value || label, order, families }),
				});
				toast.success(t("adminSportsCreated").replace("{label}", label));
			}
			resetForm();
			await load();
		} catch (error) {
			toast.error(sportConflictMessage(error, t, "admin"));
		} finally {
			setSaving(false);
		}
	};

	const promote = async (sport) => {
		if (!window.confirm(t("adminSportsConfirmPromote").replace("{label}", sport.label))) {
			return;
		}
		setBusyId(sport.sport_id);
		try {
			await fetchAPI(`/admin/sports/${encodeURIComponent(sport.sport_id)}/promote`, {
				method: "POST",
			});
			toast.success(t("adminSportsPromoted").replace("{label}", sport.label));
			await load();
		} catch (error) {
			toast.error(sportConflictMessage(error, t, "admin"));
		} finally {
			setBusyId(null);
		}
	};

	const setActive = async (sport, isActive) => {
		setBusyId(sport.sport_id);
		try {
			await fetchAPI(`/admin/sports/${encodeURIComponent(sport.sport_id)}`, {
				method: "PUT",
				body: JSON.stringify({ is_active: isActive }),
			});
			toast.success(
				(isActive ? t("sdReactivated") : t("sdDeactivated")).replace(
					"{label}",
					sport.label,
				),
			);
			await load();
		} catch (error) {
			toast.error(sportConflictMessage(error, t, "admin"));
		} finally {
			setBusyId(null);
		}
	};

	return (
		<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
			<Card className="bg-card border-border rounded-sm">
				<CardHeader className="space-y-2">
					<CardTitle className="font-heading text-xl flex items-center gap-2">
						<Activity className="w-5 h-5 text-volt" />
						{t("adminSportsTitle")}
					</CardTitle>
					<p className="text-sm text-muted-foreground">{t("adminSportsHint")}</p>
					<div className="flex flex-wrap gap-2 pt-1">
						{FILTERS.map(([key, labelKey]) => (
							<button
								key={key}
								type="button"
								aria-pressed={filter === key}
								onClick={() => setFilter(key)}
								className={`rounded-sm border px-2.5 py-1 text-xs transition-colors ${
									filter === key
										? "border-volt/60 bg-volt/15 text-volt"
										: "border-border text-muted-foreground hover:border-volt/40"
								}`}
							>
								{t(labelKey)} · {counts[key] ?? 0}
							</button>
						))}
					</div>
				</CardHeader>
				<CardContent>
					{loading ? (
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							{t("loading")}
						</div>
					) : shown.length === 0 ? (
						<p className="text-sm text-muted-foreground">{t("adminSportsEmpty")}</p>
					) : (
						<div className="space-y-2">
							{shown.map((sport) => (
								<SportRow
									key={sport.sport_id || sport.value}
									sport={sport}
									t={t}
									busy={busyId === sport.sport_id}
									onEdit={() => startEdit(sport)}
									onPromote={() => promote(sport)}
									onDeactivate={() => setDeactivating(sport)}
									onReactivate={() => setActive(sport, true)}
								/>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			<Card className="bg-card border-border rounded-sm h-fit">
				<CardHeader>
					<div className="flex items-center justify-between gap-3">
						<CardTitle className="font-heading text-xl">
							{editing ? t("adminSportsEditTitle") : t("adminSportsNewTitle")}
						</CardTitle>
						<Button
							variant="outline"
							className="rounded-sm"
							disabled={saving}
							onClick={resetForm}
						>
							<Plus className="w-4 h-4 mr-2" />
							{editing ? t("adminSportsCancelEdit") : t("adminSportsClear")}
						</Button>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					{editing?.library_scope === "personal" && (
						<p className="rounded-sm border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-200">
							{t("adminSportsEditingPersonal").replace(
								"{owner}",
								editing.owner?.name || t("scAnotherCoach"),
							)}
						</p>
					)}
					<SportDisciplineForm
						value={form}
						onChange={setForm}
						mode={editing ? "edit" : "create"}
						variant="admin"
						disabled={saving}
					/>
					<Button
						onClick={save}
						disabled={saving || !(form.label || "").trim()}
						className="w-full bg-volt hover:bg-volt/90 rounded-sm"
					>
						{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
						{editing ? t("adminSportsSave") : t("adminSportsCreate")}
					</Button>
				</CardContent>
			</Card>

			<DeactivateSportDialog
				sport={deactivating}
				open={!!deactivating}
				onOpenChange={(open) => {
					if (!open) setDeactivating(null);
				}}
				usagePath={
					deactivating
						? `/admin/sports/${encodeURIComponent(deactivating.sport_id)}/usage`
						: null
				}
				onConfirm={() => setActive(deactivating, false)}
			/>
		</div>
	);
}

function SportRow({ sport, t, busy, onEdit, onPromote, onDeactivate, onReactivate }) {
	const personal = sport.library_scope === "personal";
	const families = (
		Array.isArray(sport.families) && sport.families.length > 0
			? sport.families
			: [DOMAIN_PHYSICAL]
	)
		.map((family) => (FAMILY_KEYS[family] ? t(FAMILY_KEYS[family]) : family))
		.join(" · ");
	const usage = sport.usage || {};

	return (
		<div
			className="flex flex-col gap-2 rounded-sm bg-secondary p-3 sm:flex-row sm:items-center sm:justify-between"
			data-testid={`admin-sport-${sport.value}`}
		>
			<div className="min-w-0 space-y-1">
				<div className="flex flex-wrap items-center gap-1.5">
					<p className="font-medium">{sport.label || sport.value}</p>
					<Badge
						className={`border-0 ${
							personal ? "bg-blaze/20 text-blaze" : "bg-secondary text-muted-foreground"
						}`}
					>
						{personal
							? t("adminSportsPersonalOf").replace("{owner}", sport.owner?.name || "?")
							: t("adminSportsGlobal")}
					</Badge>
					<Badge
						className={`border-0 ${
							sport.is_active !== false
								? "bg-green-500/20 text-green-400"
								: "bg-red-500/20 text-red-400"
						}`}
					>
						{sport.is_active !== false ? t("adminSportsActive") : t("adminSportsInactive")}
					</Badge>
				</div>
				<p className="text-xs text-muted-foreground font-mono truncate">{sport.value}</p>
				<p className="text-xs text-muted-foreground">
					{families} ·{" "}
					{t("adminSportsUsage")
						.replace("{athletes}", usage.athletes ?? 0)
						.replace("{coaches}", usage.coaches ?? 0)
						.replace("{models}", usage.models ?? 0)}
				</p>
				{sport.proposed_by?.name && (
					<p className="text-xs text-muted-foreground">
						{t("adminSportsProposedBy").replace("{owner}", sport.proposed_by.name)}
					</p>
				)}
			</div>
			<div className="flex flex-wrap items-center gap-1.5 shrink-0">
				{personal && (
					<Button
						variant="outline"
						size="sm"
						className="rounded-sm"
						disabled={busy}
						onClick={onPromote}
					>
						<ArrowUpCircle className="w-3.5 h-3.5 mr-1.5" />
						{t("adminSportsPromote")}
					</Button>
				)}
				<Button
					variant="ghost"
					size="icon"
					className="rounded-sm"
					title={t("scEdit")}
					aria-label={t("scEdit")}
					disabled={busy}
					onClick={onEdit}
				>
					<Edit className="w-4 h-4" />
				</Button>
				{sport.is_active !== false ? (
					<Button
						variant="ghost"
						size="icon"
						className="rounded-sm hover:text-red-500"
						title={t("sdDeactivate")}
						aria-label={t("sdDeactivate")}
						disabled={busy}
						onClick={onDeactivate}
					>
						<EyeOff className="w-4 h-4" />
					</Button>
				) : (
					<Button
						variant="ghost"
						size="icon"
						className="rounded-sm hover:text-green-400"
						title={t("sdReactivate")}
						aria-label={t("sdReactivate")}
						disabled={busy}
						onClick={onReactivate}
					>
						<RotateCcw className="w-4 h-4" />
					</Button>
				)}
			</div>
		</div>
	);
}
